import { Injectable, inject, DOCUMENT } from '@angular/core';

/**
 * Slug shape allowlist: lowercase alphanumeric + inner hyphens, 1–63 chars
 * (DNS-label shaped, matching `Tenant.slug`). Anything else — path traversal,
 * encoded payloads, uppercase — is treated as "no tenant context" rather than
 * forwarded to the API.
 */
const TENANT_SLUG_REGEX = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * Pure URL → tenant-slug parser. Pre-bootstrap safe: no Angular DI, callable
 * from `main.ts` or an app initializer before the Router exists.
 *
 * Resolution order:
 *   1. `/t/:slug` path prefix — the canonical runtime-tenancy mount
 *      (see `apps/front/calendar/src/app/app.routes.ts`).
 *   2. `?t=<slug>` query param — legacy shim, kept for parity with the
 *      existing calendar tenant-identity precedent
 *      (private-access.component.ts reads `?t=`).
 */
export function parseTenantSlug(pathname: string, search: string): string | null {
  const pathMatch = /^\/t\/([^/]+)/.exec(pathname);
  const raw = pathMatch?.[1] ?? new URLSearchParams(search).get('t');
  if (!raw) return null;

  let candidate: string;
  try {
    candidate = decodeURIComponent(raw).trim().toLowerCase();
  } catch {
    return null; // malformed percent-encoding — not a slug
  }
  return TENANT_SLUG_REGEX.test(candidate) ? candidate : null;
}

/**
 * DI wrapper over `parseTenantSlug` for services that need the current
 * portal's tenant slug (e.g. public-event fetchers appending `tenantSlug`).
 *
 * Lives in ui-core (not the calendar tenant-context lib) on purpose:
 * `calendar-ui` is shared with the customer app, so it may only depend on
 * shared libs. In apps that never mount `/t/:slug` (admin, customer) the
 * parse simply yields `null` and behavior is unchanged.
 *
 * The slug is captured once at first injection — tenant identity is a
 * bootstrap-time property (a hard navigation is required to change portals),
 * matching how BrandingService applies theming exactly once at init.
 */
@Injectable({ providedIn: 'root' })
export class TenantSlugService {
  private readonly document = inject(DOCUMENT);

  readonly slug: string | null = parseTenantSlug(
    this.document.location?.pathname ?? '',
    this.document.location?.search ?? '',
  );
}
